import React, { useState } from 'react'

import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'

import { Drawer } from '@/components/Drawer/DrawerV2'
import Stepper from '@/components/Stepper'
import { useSendCustomerLoginMagicLinkMutation } from '@/services/customer/userApi'
import { ApplicationProfile, Profile } from '@/types/unlock'

interface CustomerMagicLinkButtonProps {
  applicationProfile: ApplicationProfile
}

export function CustomerMagicLinkButton({ applicationProfile }: CustomerMagicLinkButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [sendCustomerMagicLink] = useSendCustomerLoginMagicLinkMutation()

  const profile = applicationProfile?.profile
  const profileEmail = profile?.email

  const handleOpen = () => {
    setIsOpen(true)
    setErrorMessage(null)
  }

  const handleSubmit = () => {
    if (!profileEmail) return
    setIsSubmitting(true)
    sendCustomerMagicLink({ applicationProfileUUID: applicationProfile?.uuid })
      .unwrap()
      .then(() => {
        setIsOpen(false)
        const toastMessage = profile?.user ? 'Magic Link Sent!' : 'Welcome E-mail Sent!'
        toast.success(toastMessage, { containerId: 'feedbackLoop' })
      })
      .catch((err) => {
        setErrorMessage(err?.data?.detail || 'An error occurred.')
      })
      .finally(() => setIsSubmitting(false))
  }

  const drawerTitle = profile?.user
    ? 'Send Customer Login Magic Link'
    : 'Send Customer Welcome E-mail'
  const stepTitle = profile?.user ? 'Send Magic Link' : 'Send Welcome Email'
  const stepContent = profile?.user
    ? 'A secure magic link will be sent to the customer'
    : 'A welcome email will be sent to the customer'

  const steps = [
    {
      title: stepTitle,
      content: () => (
        <div className='text-sm text-gray-700'>
          {stepContent} <span className='font-medium text-gray-900'>{profileEmail}</span>
        </div>
      ),
      hideButton: true,
      disabledButton: !profileEmail,
    },
  ]

  return (
    <>
      <div className='flex flex-row gap-1 text-xs mb-1 mt-3 items-center justify-center'>
        <button
          data-tip
          data-for={`customerMagicLink-${profileEmail}`}
          className='btn btn-primary btn-sm'
          onClick={handleOpen}
        >
          Assist Customer to Login
        </button>
      </div>

      {isOpen && (
        <Drawer
          title={`Workflow / ${drawerTitle}`}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        >
          <Stepper
            steps={steps}
            finishButton={{
              label: 'Finish - Send',
              onClick: handleSubmit,
              loadingFooter: isSubmitting,
              footerMessage: errorMessage && <div className='text-red-500'>{errorMessage}</div>,
            }}
          />
        </Drawer>
      )}
    </>
  )
}
